import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { MdAdd } from 'react-icons/md';
import StatsCard from '@/components/StatsCard';
import ViewButton from '@/components/ViewButton';
import { DataTable } from '@podus/ui';
import type { Column } from '@podus/ui';
import AddUserModal from './AddUserModal';
import { api } from '@/lib/api';
import { exportCSV } from '@/lib/csv';
import type { StaffMember, StaffStats } from '@/types';

/* ─── Types ─── */

interface UserRow {
    id: number;
    sn: number;
    name: string;
    email: string;
    phone: string;
    dateRegistered: string;
    lastActive: string;
}

/* ─── Helpers ─── */

function formatDateTime(iso: string | null) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
}

function mapToRow(s: StaffMember, index: number): UserRow {
    return {
        id: s.id,
        sn: index + 1,
        name: s.name,
        email: s.email,
        phone: s.phone,
        dateRegistered: formatDateTime(s.createdAt),
        lastActive: formatDateTime(s.lastActive),
    };
}

/* ─── Component ─── */

export default function InternalUsers() {
    const navigate = useNavigate();
    const [showAddModal, setShowAddModal] = useState(false);
    const [users, setUsers] = useState<UserRow[]>([]);
    const [stats, setStats] = useState<StaffStats | null>(null);

    const fetchUsers = () => {
        api.get<StaffMember[]>('/api/staff?limit=100')
            .then((res) => setUsers((res.response.data ?? []).map(mapToRow)))
            .catch(() => setUsers([]));
    };

    useEffect(() => {
        fetchUsers();
        api.get<StaffStats>('/api/staff/stats')
            .then((res) => setStats(res.response.data ?? null))
            .catch(() => {});
    }, []);

    const statsCards = [
        { label: 'All Users', value: String(stats?.all ?? 0) },
        { label: 'Active Users', value: String(stats?.active ?? 0) },
        { label: 'Inactive Users', value: String(stats?.inactive ?? 0) },
    ];

    const COLUMNS: Column<UserRow>[] = [
        { key: 'sn', label: 'S/N' },
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone Number' },
        { key: 'dateRegistered', label: 'Date Registered' },
        { key: 'lastActive', label: 'Last Active' },
        {
            key: 'actions',
            label: 'Actions',
            render: (row) => <ViewButton onClick={() => navigate(`/dashboard/users/${row.id}`)} />,
        },
    ];

    return (
        <div className="space-y-6  py-6">
            <div className="flex flex-col gap-4 px-4 lg:flex-row lg:items-start lg:justify-between lg:px-8">
                <h1 className="text-2xl font-bold text-secondary sm:text-3xl">Internal Users</h1>
                <div className="grid grid-cols-3 gap-3">
                    {statsCards.map((s) => (
                        <StatsCard key={s.label} stat={s} />
                    ))}
                </div>
            </div>

            <DataTable
                title="Users"
                columns={COLUMNS}
                data={users}
                getRowKey={(row) => row.id}
                onExportCSV={() => exportCSV('internal-users', COLUMNS, users)}
                headerActions={
                    <button
                        type="button"
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center gap-2 rounded-lg bg-secondary px-4 py-2 text-[14px] font-medium text-white transition-colors hover:bg-secondary-400"
                    >
                        <MdAdd size={16} />
                        Add New User
                    </button>
                }
            />

            <AddUserModal
                open={showAddModal}
                onClose={() => {
                    setShowAddModal(false);
                    fetchUsers();
                }}
            />
        </div>
    );
}
